/** HH:mm válido para `<input type="time">` y persistencia HH:mm:ss */
const TYPE16_INPUT_RE = /^(\d{1,2}):(\d{2})$/;

export function isValidType16Input(input: string): boolean {
  if (!input) {
    return false;
  }
  const match = TYPE16_INPUT_RE.exec(String(input).trim());
  if (!match) {
    return false;
  }
  const h = Number(match[1]);
  const m = Number(match[2]);
  return h >= 0 && h <= 23 && m >= 0 && m <= 59;
}

/** Valor para `<input type="time">` (HH:mm). Legacy no parseable → vacío en UI (sin corromper BD al guardar). */
export function toType16InputValue(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }
  if (typeof value === 'object' && value !== null && 'hour' in value) {
    const hourVal = (value as { hour?: number; minute?: number }).hour;
    if (hourVal == null) {
      return '';
    }
    const h = String(hourVal).padStart(2, '0');
    const m = String((value as { minute?: number }).minute ?? 0).padStart(2, '0');
    const candidate = `${h}:${m}`;
    return isValidType16Input(candidate) ? candidate : '';
  }
  const s = String(value).trim();
  const parts = s.split(':');
  if (parts.length >= 2) {
    const candidate = `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    return isValidType16Input(candidate) ? candidate : '';
  }
  return '';
}

/** True si el valor guardado no es una hora normalizable (p. ej. "N/A"). */
export function isType16LegacyStoredValue(value: unknown): boolean {
  if (value == null || value === '') {
    return false;
  }
  return !isValidType16Input(toType16InputValue(value));
}

/** Atributo HTML `required` en `<input type="time">` (no aplicar si hay legacy no parseable). */
export function type16NativeRequired(field: { required?: boolean; value?: unknown } | null | undefined): boolean {
  if (!field?.required) {
    return false;
  }
  return !isType16LegacyStoredValue(field.value);
}

/** Persistencia: hora válida → HH:mm:ss; legacy u otro texto → se devuelve tal cual (sin ":undefined:00"). */
export function normalizeType16ForStorage(value: unknown): string {
  const input = toType16InputValue(value);
  if (isValidType16Input(input)) {
    return `${input}:00`;
  }
  if (value == null || value === '') {
    return '';
  }
  return String(value).trim();
}

/**
 * Valor a enviar al API para tipo 16.
 * - Hora válida en formulario → HH:mm:ss
 * - Legacy en BD + campo vacío sin commit en `change` → conserva stored
 * - Vacío tras evento `change` del input (userCommittedEmptyOnChange) → ''
 *
 * No usar `FormControl.dirty`: un `<input type="time">` puede quedar dirty con valor '' tras input parcial.
 */
export function resolveType16ValueForSave(
  formValue: unknown,
  storedValue: unknown,
  options: { userCommittedEmptyOnChange?: boolean } = {}
): string {
  const userCommittedEmptyOnChange = Boolean(options.userCommittedEmptyOnChange);
  const formInput = toType16InputValue(formValue);

  if (isValidType16Input(formInput)) {
    return `${formInput}:00`;
  }

  const formEmpty =
    formValue == null ||
    formValue === '' ||
    (typeof formValue === 'string' && !String(formValue).trim());

  if (formEmpty) {
    if (userCommittedEmptyOnChange) {
      return '';
    }
    if (storedValue != null && String(storedValue).trim() !== '') {
      return normalizeType16ForStorage(storedValue);
    }
    return '';
  }

  return normalizeType16ForStorage(formValue);
}

export function type48RowStoredValue(type48Field: any, subField: any, rowIndex: number): unknown {
  // El valor del grupo llega como array o como JSON en texto (así lo guarda y lo preserva
  // removeType48StoredRowAt). Sin parsear el texto, toda fila caía al valor del campo y se perdía
  // el valor propio de cada fila.
  let groupValue = type48Field?.value;
  if (typeof groupValue === 'string') {
    try {
      groupValue = JSON.parse(groupValue);
    } catch {
      groupValue = null;
    }
  }
  if (Array.isArray(groupValue) && groupValue[rowIndex] != null) {
    const row = groupValue[rowIndex];
    if (row && typeof row === 'object') {
      const id = subField.id;
      if (Object.prototype.hasOwnProperty.call(row, id)) {
        return row[id];
      }
      if (Object.prototype.hasOwnProperty.call(row, String(id))) {
        return row[String(id)];
      }
    }
  }
  return subField?.value;
}

function reindexType16CommittedEmptyRowsMap(field: any, removedRowIndex: number): void {
  const rows = field?._type16CommittedEmptyRows;
  if (!rows || typeof rows !== 'object') {
    return;
  }
  const next: Record<number, boolean> = {};
  for (const key of Object.keys(rows)) {
    const idx = Number(key);
    if (Number.isNaN(idx) || !rows[key]) {
      continue;
    }
    if (idx < removedRowIndex) {
      next[idx] = true;
    } else if (idx > removedRowIndex) {
      next[idx - 1] = true;
    }
  }
  if (Object.keys(next).length === 0) {
    delete field._type16CommittedEmptyRows;
  } else {
    field._type16CommittedEmptyRows = next;
  }
}

/** Elimina la fila en `type48Field.value` (JSON string o array) tras borrar en el FormArray. */
export function removeType48StoredRowAt(type48Field: any, removedRowIndex: number): void {
  if (!type48Field || removedRowIndex < 0) {
    return;
  }
  const raw = type48Field.value;
  if (raw == null || raw === '') {
    return;
  }

  let rows: unknown[] | null = null;
  let wasString = false;
  if (typeof raw === 'string') {
    wasString = true;
    try {
      rows = JSON.parse(raw);
    } catch {
      return;
    }
  } else if (Array.isArray(raw)) {
    rows = raw;
  } else {
    return;
  }

  if (!Array.isArray(rows) || removedRowIndex >= rows.length) {
    return;
  }
  rows.splice(removedRowIndex, 1);
  type48Field.value = wasString ? JSON.stringify(rows) : rows;
}

/** Tras removeAt en un grupo 48, realinea flags de hora vacía por índice de fila. */
export function reindexType16CommittedEmptyRowsAfterRemove(
  subFields: any[] | null | undefined,
  removedRowIndex: number
): void {
  if (!Array.isArray(subFields) || removedRowIndex < 0) {
    return;
  }
  for (const subField of subFields) {
    if (subField?.type === 16) {
      reindexType16CommittedEmptyRowsMap(subField, removedRowIndex);
    }
  }
}

/** Limpia flags de sesión UI (evita fuga entre aperturas de formulario/modal). */
export function resetType16SessionFlagsOnFields(fields: any[] | null | undefined): void {
  if (!Array.isArray(fields)) {
    return;
  }
  for (const field of fields) {
    if (!field) {
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(field, '_type16CommittedEmpty')) {
      delete field._type16CommittedEmpty;
    }
    if (Object.prototype.hasOwnProperty.call(field, '_type16CommittedEmptyRows')) {
      delete field._type16CommittedEmptyRows;
    }
    if (field.type === 48 && Array.isArray(field.customfields)) {
      resetType16SessionFlagsOnFields(field.customfields);
    }
  }
}

/** Normaliza tipo 16 en values (top-level y subcampos dentro de grupos tipo 48). */
export function applyType16NormalizationForSave(values: Record<string, any>, fields: any[] | null | undefined): void {
  if (!values || !Array.isArray(fields)) {
    return;
  }
  for (const field of fields) {
    if (!field) {
      continue;
    }
    if (field.type === 16) {
      values[field.id] = resolveType16ValueForSave(values[field.id], field.value, {
        userCommittedEmptyOnChange: Boolean(field._type16CommittedEmpty),
      });
    }
    if (field.type === 48 && Array.isArray(field.customfields) && Array.isArray(values[field.id])) {
      values[field.id].forEach((row: Record<string, unknown>, rowIndex: number) => {
        if (!row || typeof row !== 'object') {
          return;
        }
        for (const subField of field.customfields) {
          if (!subField || subField.type !== 16) {
            continue;
          }
          const subId = subField.id;
          const rowCommittedEmpty =
            subField._type16CommittedEmptyRows &&
            Object.prototype.hasOwnProperty.call(subField._type16CommittedEmptyRows, rowIndex)
              ? Boolean(subField._type16CommittedEmptyRows[rowIndex])
              : Boolean(subField._type16CommittedEmpty);
          row[subId] = resolveType16ValueForSave(row[subId], type48RowStoredValue(field, subField, rowIndex), {
            userCommittedEmptyOnChange: rowCommittedEmpty,
          });
        }
      });
    }
  }
}
